/**
 * Events Filters Component
 * Uses abstractions from config for UI components
 */

'use client';

import { FilterSearch } from '@uicwebmaster/filters';
import { ThemeBadge } from '@uicwebmaster/taxonomy-themes/components';
import { FilterThemesAutocomplete } from '@uicwebmaster/taxonomy-themes/filters';
import { X } from 'lucide-react';
import { FilterSummary } from '../components/filters/filter-summary';
import { Switch } from '../components/ui/switch';

export interface EventsFiltersProps {
  // Search
  query: string;
  onQueryChange: (value: string) => void;

  // Event Types
  eventTypes: Array<{ id: string; name: string; colour?: string | null }>;
  selectedEventTypes: string[];
  onToggleEventType: (typeId: string) => void;
  onClearEventTypeSelection: () => void;

  // Themes
  themes: Array<{
    id: string;
    name: string;
    color?: string | null;
    level?: number;
    children?: Array<{
      id: string;
      name: string;
      color?: string | null;
      level?: number;
      children?: any[];
    }>;
  }>;
  selectedThemes: string[];
  onToggleTheme: (themeId: string) => void;
  onToggleThemes?: (themeIds: string[], shouldSelect?: boolean) => void;
  onClearThemeSelection: () => void;
  themeFilterMode: 'OR' | 'AND';
  onThemeFilterModeChange: (mode: 'OR' | 'AND') => void;

  // Past events
  showPastEvents: boolean;
  onShowPastEventsChange: (show: boolean) => void;

  // Results
  totalResults: number;
  hasActiveFilters: boolean;
  onClearAllFilters: () => void;

  // Filter visibility control
  enabledFilters?: {
    search?: boolean;
    eventTypes?: boolean;
    themes?: boolean;
    pastEvents?: boolean;
  };
}

export function EventsFilters({
  query,
  onQueryChange,
  eventTypes,
  selectedEventTypes,
  onToggleEventType,
  onClearEventTypeSelection,
  themes,
  selectedThemes,
  onToggleTheme,
  onToggleThemes,
  onClearThemeSelection,
  themeFilterMode,
  onThemeFilterModeChange,
  showPastEvents,
  onShowPastEventsChange,
  totalResults,
  hasActiveFilters,
  onClearAllFilters,
  enabledFilters = {
    search: true,
    eventTypes: true,
    themes: true,
    pastEvents: true,
  },
}: EventsFiltersProps) {
  const hasSelectedEventTypes = selectedEventTypes.length > 0;

  return (
    <div className="space-y-4 sticky top-10 z-10 bg-background">
      {/* Search */}
      {enabledFilters.search && (
        <FilterSearch query={query} onQueryChange={onQueryChange} placeholder="Search events..." />
      )}

      {/* Past Events Toggle */}
      {enabledFilters.pastEvents && (
        <div className="rounded-md border p-4">
          <div className="flex items-center justify-between">
            <label htmlFor="show-past-events" className="text-sm font-medium">
              Show past events
            </label>
            <Switch
              id="show-past-events"
              checked={showPastEvents}
              onCheckedChange={onShowPastEventsChange}
            />
          </div>
        </div>
      )}

      {/* Event Types Filter with Colours */}
      {enabledFilters.eventTypes && eventTypes.length > 0 && (
        <div className="space-y-2">
          {eventTypes.map(type => {
            const isSelected = selectedEventTypes.includes(type.id);
            const colour = type.colour || '#6b7280';

            return (
              <label
                key={type.id}
                className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colours ${
                  isSelected ? '' : 'hover:bg-accent'
                }`}
                style={{
                  borderColor: isSelected ? colour : undefined,
                  backgroundColor: isSelected
                    ? colour
                      ? `${colour}15`
                      : 'hsl(var(--primary) / 0.05)'
                    : undefined,
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleEventType(type.id)}
                  className="sr-only"
                />

                {/* Colour indicator */}
                <div
                  className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: colour }}
                  aria-hidden="true"
                />

                <span
                  className="flex-1 text-sm"
                  style={{
                    color: isSelected && colour ? colour : undefined,
                    fontWeight: isSelected ? '600' : undefined,
                  }}
                >
                  {type.name}
                </span>

                {isSelected && (
                  <X className="h-4 w-4" style={{ color: colour }} aria-hidden="true" />
                )}
              </label>
            );
          })}
        </div>
      )}

      {/* Themes Filter - Autocomplete */}
      {enabledFilters.themes && themes.length > 0 && (
        <FilterThemesAutocomplete
          themes={themes}
          selectedThemes={selectedThemes}
          onToggleTheme={onToggleTheme}
          onToggleThemes={onToggleThemes}
          onClearSelection={onClearThemeSelection}
          placeholder="Select themes..."
          ThemeBadge={ThemeBadge}
        />
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <FilterSummary totalResults={totalResults} onClearAll={onClearAllFilters} />
      )}
    </div>
  );
}
